import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const CustomHeadroom = ({
  children,
  disable = false,
  upTolerance = 1,
  downTolerance = 0,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (disable) return;

    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = currentScrollY - lastScrollY;
          const scrollingDown = scrollDifference > 0;
          const scrollingUp = scrollDifference < 0;

          // Check if there's actually scrollable content (with some buffer)
          const hasScrollableContent =
            document.documentElement.scrollHeight > window.innerHeight + 20;

          // Only hide header if:
          // - There's actually scrollable content
          // - We're scrolling down significantly (more than downTolerance)
          // - We've scrolled past a minimum threshold (50px to avoid tiny movements)
          // - The scroll difference is meaningful (at least 2px)
          if (
            hasScrollableContent &&
            scrollingDown &&
            currentScrollY > Math.max(downTolerance, 50) &&
            Math.abs(scrollDifference) >= 2
          ) {
            setIsVisible(false);
          }
          // Show header when:
          // - Scrolling up more than upTolerance
          // - At the top of the page
          // - No scrollable content exists
          // - Any upward scroll movement when near the top
          else if (
            (scrollingUp && Math.abs(scrollDifference) > upTolerance) ||
            currentScrollY <= downTolerance ||
            !hasScrollableContent ||
            (scrollingUp && currentScrollY < 100)
          ) {
            setIsVisible(true);
          }

          setLastScrollY(currentScrollY);
          ticking.current = false;
        });
      }
      ticking.current = true;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [disable, upTolerance, downTolerance, lastScrollY]);

  const headroomStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    transform: `translateY(${isVisible ? '0' : '-100%'})`,
    transition: 'transform 0.3s ease-in-out',
    willChange: 'transform',
  };

  return (
    <div style={headroomStyle} {...props}>
      {children}
    </div>
  );
};

CustomHeadroom.propTypes = {
  children: PropTypes.node.isRequired,
  disable: PropTypes.bool,
  upTolerance: PropTypes.number,
  downTolerance: PropTypes.number,
};

export default CustomHeadroom;
